(function () {
  "use strict";

  var BLUE_COLORS = [
    [0, 108, 255],
    [0, 140, 255],
    [0, 150, 242],
    [0, 126, 224]
  ];
  var CYAN_COLORS = [
    [0, 184, 217],
    [0, 213, 196],
    [24, 224, 194]
  ];
  var BRIGHT_COLORS = [
    [104, 242, 220],
    [36, 226, 215],
    [0, 213, 196]
  ];
  var TWO_PI = Math.PI * 2;
  var instances = [];
  var metrics = window.__aiParticleHeroMetrics || {};
  window.__aiParticleHeroMetrics = metrics;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function readNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function isLowPower() {
    var cores = navigator.hardwareConcurrency || 4;
    var memory = navigator.deviceMemory || 4;
    return cores <= 4 || memory <= 4;
  }

  function chooseColor(mix) {
    var source = mix < 0.65 ? BLUE_COLORS : mix < 0.9 ? CYAN_COLORS : BRIGHT_COLORS;
    return source[(Math.random() * source.length) | 0];
  }

  function getProfile(width, density) {
    var ratio = clamp(density, 55, 125) / 100;
    var lowRatio = isLowPower() ? 0.78 : 1;

    if (width < 640) {
      return {
        kind: "mobile",
        count: Math.round(clamp(240 * ratio * lowRatio, 220, 260)),
        bands: 3,
        blobs: Math.round(clamp(5 * lowRatio, 4, 6)),
        dprMax: 1.25,
        frameInterval: 24,
        glow: 0.82,
        leftQuiet: 0.52,
        driftScale: 0.72
      };
    }

    if (width < 1024) {
      return {
        kind: "tablet",
        count: Math.round(clamp(440 * ratio * lowRatio, 380, 480)),
        bands: 4,
        blobs: Math.round(clamp(8 * lowRatio, 6, 9)),
        dprMax: 1.5,
        frameInterval: 20,
        glow: 0.9,
        leftQuiet: 0.46,
        driftScale: 0.88
      };
    }

    return {
      kind: "desktop",
      count: Math.round(clamp(690 * ratio * lowRatio, 600, 750)),
      bands: 5,
      blobs: Math.round(clamp(11 * lowRatio, 8, 14)),
      dprMax: 1.75,
      frameInterval: 16,
      glow: 1,
      leftQuiet: 0.42,
      driftScale: 1
    };
  }

  function ParticleHero(root) {
    this.root = root;
    this.canvas = root.querySelector("canvas");
    this.ctx = this.canvas && this.canvas.getContext("2d", { alpha: true });
    this.particles = [];
    this.blobs = [];
    this.bands = [];
    this.pointer = { x: 0, y: 0, tx: 0, ty: 0, active: false };
    this.size = { width: 0, height: 0, dpr: 1 };
    this.frame = 0;
    this.lastTime = 0;
    this.lastDraw = 0;
    this.fpsFrames = 0;
    this.fpsTime = 0;
    this.fps = 0;
    this.lowFpsTicks = 0;
    this.degraded = false;
    this.inView = true;
    this.destroyed = false;
    this.resizeTimer = 0;
    this.scrollY = window.scrollY || 0;
    this.startTime = 0;

    this.settings = {
      enabled: root.dataset.particleEnabled !== "false",
      mobileEnabled: root.dataset.mobileEnabled !== "false",
      density: readNumber(root.dataset.density, 100),
      speed: readNumber(root.dataset.speed, 60) / 100,
      brightness: readNumber(root.dataset.brightness, 72) / 100,
      glow: readNumber(root.dataset.glow, 34) / 100,
      reducedMode: root.dataset.reducedMode || "static"
    };

    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.staticMode = this.reducedMotion && this.settings.reducedMode !== "animate";

    if (!this.canvas || !this.ctx || !this.settings.enabled) return;
    if (window.innerWidth < 640 && !this.settings.mobileEnabled) return;

    this.onResize = this.onResize.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.tick = this.tick.bind(this);

    this.resize();
    this.addListeners();
    this.draw(0);
    if (!this.staticMode) this.start();
  }

  ParticleHero.prototype.addListeners = function () {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    document.addEventListener("visibilitychange", this.onVisibility);

    if (!this.staticMode) {
      this.root.addEventListener("pointermove", this.onPointerMove, { passive: true });
      this.root.addEventListener("pointerleave", this.onPointerLeave, { passive: true });
      this.root.addEventListener("touchmove", this.onTouchMove, { passive: true });
    }

    if ("IntersectionObserver" in window) {
      var self = this;
      this.observer = new IntersectionObserver(function (entries) {
        self.inView = entries[0] && entries[0].isIntersecting;
        if (self.inView && !self.staticMode) self.start();
      }, { threshold: 0.06 });
      this.observer.observe(this.root);
    }
  };

  ParticleHero.prototype.onResize = function () {
    var self = this;
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(function () {
      self.resize();
      self.draw(0);
    }, 180);
  };

  ParticleHero.prototype.onScroll = function () {
    this.scrollY = window.scrollY || 0;
  };

  ParticleHero.prototype.onVisibility = function () {
    if (!document.hidden && this.inView && !this.staticMode) this.start();
  };

  ParticleHero.prototype.onPointerMove = function (event) {
    var rect = this.root.getBoundingClientRect();
    this.pointer.tx = clamp(((event.clientX - rect.left) / rect.width - 0.55) * 20, -18, 18);
    this.pointer.ty = clamp(((event.clientY - rect.top) / rect.height - 0.45) * 18, -14, 14);
    this.pointer.active = true;
  };

  ParticleHero.prototype.onPointerLeave = function () {
    this.pointer.tx = 0;
    this.pointer.ty = 0;
    this.pointer.active = false;
  };

  ParticleHero.prototype.onTouchMove = function (event) {
    if (!event.touches || !event.touches[0]) return;
    var touch = event.touches[0];
    var rect = this.root.getBoundingClientRect();
    this.pointer.tx = clamp(((touch.clientX - rect.left) / rect.width - 0.5) * 12, -10, 10);
    this.pointer.ty = clamp(((touch.clientY - rect.top) / rect.height - 0.45) * 10, -8, 8);
    this.pointer.active = true;
  };

  ParticleHero.prototype.resize = function () {
    var rect = this.root.getBoundingClientRect();
    var width = Math.max(1, Math.round(rect.width));
    var height = Math.max(1, Math.round(rect.height));
    var profile = getProfile(width, this.settings.density);
    var dpr = Math.min(window.devicePixelRatio || 1, profile.dprMax);

    this.size.width = width;
    this.size.height = height;
    this.size.dpr = dpr;
    this.profile = profile;
    this.startTime = performance.now();

    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.style.width = width + "px";
    this.canvas.style.height = height + "px";
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.seedBands();
    this.seedParticles(profile.count);
    this.seedBlobs(profile.blobs);
    this.writeMetrics();
  };

  ParticleHero.prototype.seedBands = function () {
    this.bands.length = 0;
    var width = this.size.width;
    var height = this.size.height;
    var count = this.profile.bands;

    for (var i = 0; i < count; i += 1) {
      this.bands.push({
        baseY: height * (0.68 - i * 0.105 + (Math.random() - 0.5) * 0.08),
        slope: height * (-0.26 - Math.random() * 0.16),
        amplitude: height * (0.055 + Math.random() * 0.07),
        frequency: 1.25 + Math.random() * 1.15,
        phase: Math.random() * TWO_PI,
        width: width * (0.05 + Math.random() * 0.035),
        brightness: 0.55 + Math.random() * 0.35
      });
    }
  };

  ParticleHero.prototype.bandY = function (band, x, time) {
    var normalizedX = x / Math.max(1, this.size.width);
    var wave = Math.sin(normalizedX * band.frequency * Math.PI + band.phase + time * 0.00008) * band.amplitude;
    return band.baseY + band.slope * (normalizedX - 0.5) - wave;
  };

  ParticleHero.prototype.seedParticles = function (targetCount) {
    if (this.particles.length > targetCount) this.particles.length = targetCount;
    while (this.particles.length < targetCount) this.particles.push({});

    for (var i = 0; i < this.particles.length; i += 1) {
      this.resetParticle(this.particles[i], true);
    }
  };

  ParticleHero.prototype.seedBlobs = function (targetCount) {
    if (this.blobs.length > targetCount) this.blobs.length = targetCount;
    while (this.blobs.length < targetCount) this.blobs.push({});

    for (var i = 0; i < this.blobs.length; i += 1) {
      this.resetBlob(this.blobs[i], true);
    }
  };

  ParticleHero.prototype.resetParticle = function (p, initial) {
    var width = this.size.width;
    var height = this.size.height;
    var layerPick = Math.random();
    var banded = Math.random() < 0.74;
    var layer = layerPick < 0.52 ? 0 : layerPick < 0.86 ? 1 : layerPick < 0.97 ? 2 : 3;
    var colorMix = Math.random();
    var x = initial ? Math.random() * width : -40 - Math.random() * width * 0.12;
    var leftZone = width * this.profile.leftQuiet;

    if (initial && x < leftZone && Math.random() < 0.58) {
      x = leftZone + Math.random() * (width - leftZone);
    }

    p.x = x;
    p.depth = layer === 0 ? 0.22 + Math.random() * 0.28 : layer === 1 ? 0.48 + Math.random() * 0.28 : layer === 2 ? 0.78 + Math.random() * 0.24 : 1.08;
    p.layer = layer;
    p.colorMix = colorMix;
    p.color = chooseColor(colorMix);
    p.drift = 0.00055 + Math.random() * 0.00125;
    p.phase = Math.random() * TWO_PI;
    p.brightness = 0.72 + Math.random() * 0.56;
    p.pulseSpeed = 0.00022 + Math.random() * 0.00034;
    p.pulseAmount = layer >= 2 || colorMix > 0.9 ? 0.12 + Math.random() * 0.12 : 0.04 + Math.random() * 0.06;

    if (layer === 0) {
      p.size = 0.5 + Math.random() * 0.7;
      p.opacity = 0.1 + Math.random() * 0.2;
      p.speedX = (0.015 + Math.random() * 0.025) * this.profile.driftScale;
      p.speedY = (-0.006 + Math.random() * 0.015) * this.profile.driftScale;
    } else if (layer === 1) {
      p.size = 1 + Math.random() * 1.5;
      p.opacity = 0.3 + Math.random() * 0.38;
      p.speedX = (0.028 + Math.random() * 0.043) * this.profile.driftScale;
      p.speedY = (-0.018 + Math.random() * 0.04) * this.profile.driftScale;
    } else if (layer === 2) {
      p.size = 2.5 + Math.random() * 2.5;
      p.opacity = 0.45 + Math.random() * 0.34;
      p.speedX = (0.018 + Math.random() * 0.035) * this.profile.driftScale;
      p.speedY = (-0.024 + Math.random() * 0.05) * this.profile.driftScale;
    } else {
      p.size = 5 + Math.random() * 9;
      p.opacity = 0.18 + Math.random() * 0.26;
      p.speedX = (0.009 + Math.random() * 0.016) * this.profile.driftScale;
      p.speedY = (-0.01 + Math.random() * 0.02) * this.profile.driftScale;
    }

    if (banded) {
      p.bandIndex = (Math.random() * this.bands.length) | 0;
      p.bandOffset = (Math.random() - 0.5) * this.bands[p.bandIndex].width * (layer === 0 ? 3.2 : layer === 1 ? 2.2 : 1.4);
      p.bandPull = layer === 0 ? 0.012 : layer === 1 ? 0.026 : 0.018;
      p.y = this.bandY(this.bands[p.bandIndex], p.x, 0) + p.bandOffset;
    } else {
      p.bandIndex = -1;
      p.bandOffset = 0;
      p.bandPull = 0;
      p.y = height * (0.08 + Math.random() * 0.84);
    }

    p.baseOpacity = p.opacity;
    this.applyTextQuietZone(p);
  };

  ParticleHero.prototype.resetBlob = function (b, initial) {
    var width = this.size.width;
    var height = this.size.height;
    var mobile = this.profile.kind === "mobile";

    b.x = initial ? width * (0.42 + Math.random() * 0.62) : -80 - Math.random() * 80;
    b.y = height * (0.12 + Math.random() * 0.68);
    b.depth = 0.55 + Math.random() * 0.55;
    b.size = (mobile ? 5 : 7) + Math.random() * (mobile ? 8 : 10);
    b.blur = b.size * (1.8 + Math.random() * 1.8);
    b.opacity = 0.08 + Math.random() * 0.16;
    b.speedX = (0.006 + Math.random() * 0.014) * this.profile.driftScale;
    b.speedY = (-0.006 + Math.random() * 0.012) * this.profile.driftScale;
    b.phase = Math.random() * TWO_PI;
    b.drift = 0.00028 + Math.random() * 0.00042;
    b.pulseSpeed = 0.00016 + Math.random() * 0.0002;
    b.color = chooseColor(0.9 + Math.random() * 0.1);
  };

  ParticleHero.prototype.applyTextQuietZone = function (p) {
    if (p.x < this.size.width * this.profile.leftQuiet) {
      p.opacity = p.baseOpacity * (this.profile.kind === "mobile" ? 0.72 : 0.4);
      p.size *= this.profile.kind === "mobile" ? 0.92 : 0.82;
    }
  };

  ParticleHero.prototype.start = function () {
    if (this.frame || this.destroyed) return;
    this.lastTime = performance.now();
    this.frame = window.requestAnimationFrame(this.tick);
  };

  ParticleHero.prototype.tick = function (time) {
    this.frame = 0;
    if (this.destroyed || document.hidden || !this.inView) return;

    var elapsed = time - this.lastDraw;
    if (elapsed < this.profile.frameInterval) {
      this.frame = window.requestAnimationFrame(this.tick);
      return;
    }

    var delta = Math.min(48, time - this.lastTime || 16);
    this.lastTime = time;
    this.lastDraw = time;
    this.update(delta, time);
    this.draw(time);
    this.trackFps(time);
    this.frame = window.requestAnimationFrame(this.tick);
  };

  ParticleHero.prototype.trackFps = function (time) {
    this.fpsFrames += 1;
    if (!this.fpsTime) this.fpsTime = time;
    if (time - this.fpsTime > 1000) {
      this.fps = Math.round((this.fpsFrames * 1000) / (time - this.fpsTime));
      this.fpsFrames = 0;
      this.fpsTime = time;
      var threshold = this.profile.kind === "desktop" ? 34 : 24;
      if (this.fps < threshold) {
        this.lowFpsTicks += 1;
        if (this.lowFpsTicks >= 3) this.degrade();
      } else {
        this.lowFpsTicks = 0;
      }
      this.writeMetrics();
    }
  };

  ParticleHero.prototype.degrade = function () {
    if (this.degraded) return;
    this.degraded = true;
    var target = Math.max(180, Math.round(this.particles.length * 0.76));
    this.particles.length = target;
    this.blobs.length = Math.max(3, Math.round(this.blobs.length * 0.7));
    this.profile.frameInterval = Math.max(this.profile.frameInterval, this.profile.kind === "desktop" ? 22 : 30);
    this.writeMetrics();
  };

  ParticleHero.prototype.update = function (delta, time) {
    var dt = (delta / 16.67) * this.settings.speed;
    var width = this.size.width;
    var height = this.size.height;
    var scrollOffset = ((this.scrollY || 0) % 700) * 0.002;

    this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.055;
    this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.055;

    for (var i = 0; i < this.particles.length; i += 1) {
      var p = this.particles[i];
      p.x += p.speedX * dt * 16.67;
      p.y += p.speedY * dt * 16.67;
      p.y += Math.sin(time * p.drift + p.phase) * 0.08;

      if (p.bandIndex >= 0 && this.bands[p.bandIndex]) {
        var targetY = this.bandY(this.bands[p.bandIndex], p.x, time) + p.bandOffset;
        p.y += (targetY - p.y) * p.bandPull * dt;
      }

      if (p.x > width + 70 || p.y < -90 || p.y > height + 90) {
        this.resetParticle(p, false);
      }
    }

    for (var b = 0; b < this.blobs.length; b += 1) {
      var blob = this.blobs[b];
      blob.x += blob.speedX * dt * 16.67;
      blob.y += blob.speedY * dt * 16.67 + Math.sin(time * blob.drift + blob.phase) * 0.045;
      if (blob.x > width + 120 || blob.y < -120 || blob.y > height + 120) {
        this.resetBlob(blob, false);
      }
    }

    this.scrollDepth = scrollOffset;
  };

  ParticleHero.prototype.draw = function (time) {
    var ctx = this.ctx;
    var width = this.size.width;
    var height = this.size.height;

    ctx.clearRect(0, 0, width, height);
    this.drawBackground(ctx, width, height);

    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    this.drawBandMist(ctx, time);
    this.drawParticles(ctx, time);
    this.drawBlobs(ctx, time);
    ctx.restore();
  };

  ParticleHero.prototype.drawBackground = function (ctx, width, height) {
    var glowA = ctx.createRadialGradient(width * 0.74, height * 0.34, 0, width * 0.74, height * 0.34, Math.max(width, height) * 0.56);
    glowA.addColorStop(0, "rgba(0, 213, 196, " + (0.13 * this.settings.glow).toFixed(3) + ")");
    glowA.addColorStop(0.38, "rgba(0, 108, 255, " + (0.09 * this.settings.glow).toFixed(3) + ")");
    glowA.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = glowA;
    ctx.fillRect(0, 0, width, height);

    var glowB = ctx.createRadialGradient(width * 0.58, height * 0.72, 0, width * 0.58, height * 0.72, Math.max(width, height) * 0.48);
    glowB.addColorStop(0, "rgba(0, 184, 217, " + (0.06 * this.settings.glow).toFixed(3) + ")");
    glowB.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = glowB;
    ctx.fillRect(0, 0, width, height);
  };

  ParticleHero.prototype.drawBandMist = function (ctx, time) {
    var width = this.size.width;
    var steps = this.profile.kind === "mobile" ? 38 : this.profile.kind === "tablet" ? 52 : 66;

    for (var b = 0; b < this.bands.length; b += 1) {
      var band = this.bands[b];
      for (var i = 0; i < steps; i += 1) {
        var x = (i / (steps - 1)) * width;
        var y = this.bandY(band, x, time);
        var centerFade = 1 - Math.abs(i / (steps - 1) - 0.62) * 1.35;
        var alpha = clamp(centerFade, 0, 1) * 0.028 * band.brightness * this.settings.brightness;
        var size = band.width * (0.15 + Math.sin(time * 0.00012 + band.phase + i) * 0.018);
        ctx.fillStyle = "rgba(0, 213, 196, " + alpha.toFixed(3) + ")";
        ctx.beginPath();
        ctx.arc(x, y, size, 0, TWO_PI);
        ctx.fill();
      }
    }
  };

  ParticleHero.prototype.pointerOffset = function (p) {
    var depth = p.depth || 0.5;
    return {
      x: this.pointer.x * (0.22 + depth * 0.32),
      y: this.pointer.y * (0.18 + depth * 0.26) + (this.scrollDepth || 0) * depth
    };
  };

  ParticleHero.prototype.drawParticles = function (ctx, time) {
    for (var i = 0; i < this.particles.length; i += 1) {
      var p = this.particles[i];
      var offset = this.pointerOffset(p);
      var pulse = 1 + Math.sin(time * p.pulseSpeed + p.phase) * p.pulseAmount;
      var alpha = clamp(p.opacity * p.brightness * pulse * this.settings.brightness, 0.04, 0.88);
      var radius = p.size * (0.86 + p.depth * 0.2);
      var x = p.x + offset.x;
      var y = p.y + offset.y;

      ctx.fillStyle = "rgba(" + p.color[0] + "," + p.color[1] + "," + p.color[2] + "," + alpha.toFixed(3) + ")";
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, TWO_PI);
      ctx.fill();
    }
  };

  ParticleHero.prototype.drawBlobs = function (ctx, time) {
    for (var i = 0; i < this.blobs.length; i += 1) {
      var b = this.blobs[i];
      var pulse = 1 + Math.sin(time * b.pulseSpeed + b.phase) * 0.18;
      var alpha = clamp(b.opacity * pulse * this.settings.brightness, 0.04, 0.24);
      var offsetX = this.pointer.x * (0.18 + b.depth * 0.18);
      var offsetY = this.pointer.y * (0.12 + b.depth * 0.16);
      var x = b.x + offsetX;
      var y = b.y + offsetY;
      var gradient = ctx.createRadialGradient(x, y, 0, x, y, b.size + b.blur);

      gradient.addColorStop(0, "rgba(" + b.color[0] + "," + b.color[1] + "," + b.color[2] + "," + alpha.toFixed(3) + ")");
      gradient.addColorStop(0.42, "rgba(" + b.color[0] + "," + b.color[1] + "," + b.color[2] + "," + (alpha * 0.34).toFixed(3) + ")");
      gradient.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, b.size + b.blur, 0, TWO_PI);
      ctx.fill();
    }
  };

  ParticleHero.prototype.writeMetrics = function () {
    var blue = 0;
    var cyan = 0;
    var bright = 0;

    for (var i = 0; i < this.particles.length; i += 1) {
      if (this.particles[i].colorMix < 0.65) blue += 1;
      else if (this.particles[i].colorMix < 0.9) cyan += 1;
      else bright += 1;
    }

    metrics.count = this.particles.length;
    metrics.fps = this.fps;
    metrics.width = this.size.width;
    metrics.height = this.size.height;
    metrics.mobile = this.profile.kind === "mobile";
    metrics.profile = this.profile.kind;
    metrics.staticMode = this.staticMode;
    metrics.bands = this.bands.length;
    metrics.blobs = this.blobs.length;
    metrics.blueRatio = this.particles.length ? Number((blue / this.particles.length).toFixed(2)) : 0;
    metrics.cyanRatio = this.particles.length ? Number((cyan / this.particles.length).toFixed(2)) : 0;
    metrics.brightRatio = this.particles.length ? Number((bright / this.particles.length).toFixed(2)) : 0;
    metrics.degraded = this.degraded;
    metrics.flow = "galaxy";
  };

  ParticleHero.prototype.destroy = function () {
    this.destroyed = true;
    if (this.frame) window.cancelAnimationFrame(this.frame);
    window.clearTimeout(this.resizeTimer);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.root.removeEventListener("pointermove", this.onPointerMove);
    this.root.removeEventListener("pointerleave", this.onPointerLeave);
    this.root.removeEventListener("touchmove", this.onTouchMove);
    if (this.observer) this.observer.disconnect();
  };

  function init() {
    var roots = document.querySelectorAll("[data-ai-particle-hero]");
    for (var i = 0; i < roots.length; i += 1) {
      if (!roots[i].__aiParticleHero) {
        roots[i].__aiParticleHero = new ParticleHero(roots[i]);
        instances.push(roots[i].__aiParticleHero);
      }
    }
  }

  function scheduleInit() {
    var run = function () {
      var width = window.innerWidth || 1024;
      if (width < 640) {
        window.setTimeout(init, 1600);
      } else if (width < 1024) {
        window.setTimeout(init, 900);
      } else if ("requestIdleCallback" in window) {
        window.requestIdleCallback(init, { timeout: 900 });
      } else {
        window.setTimeout(init, 350);
      }
    };

    if (document.readyState === "complete") {
      run();
    } else {
      window.addEventListener("load", run, { once: true });
    }
  }

  window.addEventListener("pagehide", function () {
    for (var i = 0; i < instances.length; i += 1) instances[i].destroy();
    instances.length = 0;
  });

  scheduleInit();
})();
